import { type RouteById, type SuccessOf, VIDEO_MAX_BYTES } from "@volgo/core/contract";
import type { ApiClient } from "../api/client";

export type UploadTicket = SuccessOf<RouteById<"videos.create">>;
export const CHUNK = 16 * 1024 * 1024;
export class UploadError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "UploadError";
  }
}
const rejected = (status: number) => (status >= 400 && status < 500 && status !== 409) || status === 507;
export function fileError(file: Pick<File, "name" | "size">): string | null {
  if (!/\.(mp4|m4v|mov|mkv|webm|avi|wmv|flv|ts|m2ts|3gp)$/i.test(file.name))
    return "지원하는 영상 파일을 선택하세요.";
  if (file.size > VIDEO_MAX_BYTES)
    return `파일은 최대 ${VIDEO_MAX_BYTES / 1024 ** 3}GiB까지 올릴 수 있습니다.`;
  if (file.size === 0) return "빈 파일은 올릴 수 없습니다.";
  return null;
}
export async function uploadFile(
  client: ApiClient,
  ticket: UploadTicket,
  file: File,
  signal: AbortSignal,
  onProgress: (offset: number, rate: number) => void,
) {
  if (Date.parse(ticket.expiresAt) <= Date.now())
    throw new UploadError("업로드가 만료되었습니다. 기존 업로드 취소 후 새로 올려 주세요.", 410);
  const started = performance.now();
  let sent = 0;
  const request = (method: "HEAD" | "PATCH", offset?: number, body?: Blob) =>
    client.media(ticket.uploadUrl, ticket.uploadToken, method, offset, body, signal);
  const offsetOf = (response: Response) => {
    const raw = response.headers.get("Upload-Offset");
    const offset = Number(raw);
    if (raw === null || !Number.isSafeInteger(offset) || offset < 0 || offset > file.size)
      throw new Error("서버가 올바른 업로드 위치를 보내지 않았습니다.");
    return offset;
  };
  const head = await request("HEAD");
  if (!head.ok) throw new UploadError(`이어 올리기 위치 확인 실패 (${head.status})`, head.status);
  if (Number(head.headers.get("Upload-Length")) !== file.size)
    throw new Error("서버의 파일 크기가 선택한 파일과 다릅니다.");
  let offset = offsetOf(head);
  onProgress(offset, 0);
  while (offset < file.size) {
    if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
    if (Date.parse(ticket.expiresAt) <= Date.now())
      throw new UploadError("업로드가 만료되었습니다. 기존 업로드 취소 후 새로 올려 주세요.", 410);
    const end = Math.min(file.size, offset + CHUNK);
    let advanced = false;
    let lastError: unknown;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const response = await request("PATCH", offset, file.slice(offset, end));
        if (!response.ok && response.status !== 409)
          throw new UploadError(`조각 전송 실패 (${response.status})`, response.status);
        const next = offsetOf(response);
        if (response.ok && next !== end) throw new Error("서버의 수신 위치가 전송한 조각과 다릅니다.");
        if (next === offset && response.status === 409) throw new Error("업로드 위치가 맞지 않습니다.");
        sent += Math.max(0, next - offset);
        offset = next;
        advanced = true;
        break;
      } catch (e) {
        if (signal.aborted) throw e;
        if (e instanceof UploadError && rejected(e.status)) throw e;
        lastError = e;
        // A response may be lost after the write committed. Always ask the server before retrying.
        if (attempt < 3) {
          try {
            const current = await request("HEAD");
            if (!current.ok && rejected(current.status))
              throw new UploadError(`이어 올리기 위치 확인 실패 (${current.status})`, current.status);
            if (current.ok) {
              const next = offsetOf(current);
              if (next !== offset) {
                sent += Math.max(0, next - offset);
                offset = next;
                advanced = true;
                break;
              }
            }
          } catch (headError) {
            if (signal.aborted) throw headError;
            if (headError instanceof UploadError && rejected(headError.status)) throw headError;
            lastError = headError;
          }
        }
      }
    }
    if (!advanced) throw lastError ?? new Error("전송이 중단되었습니다. 이어 올리기를 눌러 주세요.");
    onProgress(offset, sent / Math.max(0.001, (performance.now() - started) / 1000));
  }
}
